import { RESET_OPTION, CONSULT_TABS } from 'features/consults/utils/constants';
import {
  ServiceTypeEnum,
  ConsultAttestationEnum,
  EegConsultTypes,
  NeuroConsultTypes,
} from 'utils/enums';

import demographicsFields, {
  teleNeuroDemographicsFields,
  eegDemographicsFields,
} from 'features/consults/shared/tabs/Demographics/formFields';
import eegFields from 'features/consults/pages/eeg/tabs/EEG/formFields';
import signatureFields from 'features/consults/shared/tabs/Signature/formFields';
import {
  clinicAssessmentFields,
  neuroAssessmentFields,
} from '../shared/tabs/Assessment/formFields';

export const hasR1Integration = (configs, consultType = null) => {
  if (!Array.isArray(configs)) return false;

  return configs.some((config) => {
    if (config.target !== 'R1') return false;
    if (!consultType) return true;
    return config.consultTypes.includes(consultType);
  });
};

export const isEmptyValue = (value) => {
  const emptyValues = ['', RESET_OPTION.value, null, undefined];
  return Array.isArray(value) ? !value.length : emptyValues.includes(value);
};

export const neuroSignatureRequiredFields = (consult) => {
  const attestationValue =
    consult[CONSULT_TABS.SIGNATURE]?.[
      signatureFields.attestationOfConsultCompletion.name
    ];

  const isTotalTimeRequired = [
    ConsultAttestationEnum.videoConsultEmr,
    ConsultAttestationEnum.videoConsultTechDifficulty,
    ConsultAttestationEnum.videoConsultFax,
  ].includes(attestationValue);

  return [
    signatureFields.attestationOfConsultCompletion,
    ...(isTotalTimeRequired ? [signatureFields.totalFaceToFaceTime] : []),
  ];
};

export const teleNeuroSignatureRequiredFields = (consult) => {
  const attestationValue =
    consult[CONSULT_TABS.SIGNATURE]?.[
      signatureFields.attestationOfConsultCompletion.name
    ];

  const attestationTriggers = [
    ConsultAttestationEnum.appVideo,
    ConsultAttestationEnum.mdVideo,
    ConsultAttestationEnum.appAndMDVideo,
  ];

  if (!attestationTriggers.includes(attestationValue)) {
    return [signatureFields.attestationOfConsultCompletion];
  }

  const isTotalTimeRequired = attestationTriggers.includes(attestationValue);

  return [
    signatureFields.attestationOfConsultCompletion,
    ...(isTotalTimeRequired ? [signatureFields.totalFaceToFaceTime] : []),
  ];
};

export const neuroAssessmentRequiredFields = (consult) => {
  const attestationValue =
    consult[CONSULT_TABS.SIGNATURE]?.[
      signatureFields.attestationOfConsultCompletion.name
    ];

  const consultTypeValue = consult[CONSULT_TABS.DEMOGRAPHICS]?.consultType;

  const isFollowUpPhoneCall =
    attestationValue === ConsultAttestationEnum.followUpPhoneCall;
  const isFollowUpConsultType =
    consultTypeValue === NeuroConsultTypes.FOLLOW_UP;

  return isFollowUpPhoneCall || isFollowUpConsultType
    ? []
    : [neuroAssessmentFields.impressionPredefined];
};

export const isEegcptCodeCodeRequired = (consult, consultFacility) => {
  const attestationValue =
    consult[CONSULT_TABS.SIGNATURE]?.[
      signatureFields.attestationOfEEGCompletion.name
    ];
  const attestationException =
    consult?.continousEegValidationFacilityIdsModel?.attestationExceptionModel;

  if (attestationException) {
    const isExceptionAttestation =
      attestationValue === attestationException.attestationExceptionValue;
    const isExceptionFacility =
      attestationException.attestationExceptionFacilityIds?.includes(
        consultFacility?.id,
      );

    if (isExceptionAttestation && isExceptionFacility) {
      return false;
    }
  }

  const type =
    consult?.[CONSULT_TABS.DEMOGRAPHICS]?.consultType ??
    consult?.[CONSULT_TABS.DEMOGRAPHICS]?.eegPediatricConsultType;
  const subtype = consult?.[CONSULT_TABS.DEMOGRAPHICS]?.observationType;
  const billingOn = hasR1Integration(
    consultFacility?.facilityIntegrationConfigurations,
    consult?.type,
  );

  let additionalCheck = false;

  const requiredTypes = [
    EegConsultTypes.ROUTINE,
    EegConsultTypes.PROLONGED,
    EegConsultTypes.AEEG,
  ];
  if (requiredTypes.includes(type)) {
    additionalCheck = true;
  }

  if (
    (type === EegConsultTypes.CERIBELL || type === EegConsultTypes.ZETO) &&
    subtype === 'DisconnectedEndClip'
  ) {
    additionalCheck = true;
  }

  if (
    type === EegConsultTypes.CEEG &&
    subtype === 'DisconnectedEndClip' &&
    billingOn
  ) {
    additionalCheck = true;
  }

  if (additionalCheck) {
    if (attestationException) {
      return attestationException.attestationExceptionFacilityIds?.includes(
        consultFacility?.id,
      ) ?? false;
    }
    return true;
  }

  return false;
};

export const isCptCodeMultiSelect = (consult, consultFacility) => {
  const type =
    consult?.[CONSULT_TABS.DEMOGRAPHICS]?.consultType ??
    consult?.[CONSULT_TABS.DEMOGRAPHICS]?.eegPediatricConsultType;
  const billingOn = hasR1Integration(
    consultFacility?.facilityIntegrationConfigurations,
    consult?.type,
  );
  const typesAllowingMulti = [
    EegConsultTypes.CEEG,
    EegConsultTypes.CERIBELL,
    EegConsultTypes.ZETO,
  ];
  return typesAllowingMulti.includes(type) && billingOn;
};

const eegRequiredFields = (consult, consultFacility, isPeds) => {
  const consultType =
    consult?.type === ServiceTypeEnum.PEDS_EEG
      ? consult[CONSULT_TABS.DEMOGRAPHICS]?.eegPediatricConsultType
      : consult[CONSULT_TABS.DEMOGRAPHICS]?.consultType;

  const demographicFields = [
    demographicsFields.firstName,
    demographicsFields.lastName,
    demographicsFields.facility,
    isPeds
      ? eegDemographicsFields.eegPediatricConsultType
      : demographicsFields.consultType,
  ];

  if (
    [
      EegConsultTypes.CEEG,
      EegConsultTypes.CERIBELL,
      EegConsultTypes.ZETO,
    ].includes(consultType)
  ) {
    demographicFields.push(demographicsFields.observationType);
  }

  return {
    [CONSULT_TABS.DEMOGRAPHICS]: demographicFields,
    [CONSULT_TABS.EEG]: [
      eegFields.startTimeOfEEGReadAt,
      eegFields.stopTimeOfEEGReadAt,
      eegFields.duration,
    ],
    [CONSULT_TABS.SIGNATURE]: [
      signatureFields.attestationOfEEGCompletion,
      ...(isEegcptCodeCodeRequired(consult, consultFacility)
        ? [signatureFields.eegcptCodes, signatureFields.icdCodes]
        : []),
    ],
  };
};

const requiredConsultFields = (consult, consultFacility) => ({
  [ServiceTypeEnum.NEURO]: {
    [CONSULT_TABS.DEMOGRAPHICS]: [
      demographicsFields.consultType,
      demographicsFields.neuroPatientLocation,
      demographicsFields.firstName,
      demographicsFields.lastName,
      demographicsFields.facility,
    ],
    [CONSULT_TABS.ASSESSMENT]: neuroAssessmentRequiredFields(consult),
    [CONSULT_TABS.SIGNATURE]: neuroSignatureRequiredFields(
      consult,
      consultFacility,
    ),
  },
  [ServiceTypeEnum.TELE_NEURO]: {
    [CONSULT_TABS.DEMOGRAPHICS]: [
      teleNeuroDemographicsFields.consultType,
      teleNeuroDemographicsFields.firstName,
      teleNeuroDemographicsFields.lastName,
      teleNeuroDemographicsFields.dateOfBirth,
      teleNeuroDemographicsFields.facility,
      teleNeuroDemographicsFields.dateOfAdmission,
      teleNeuroDemographicsFields.dateOfInitialConsultation,
      teleNeuroDemographicsFields.dateOfService,
    ],
    [CONSULT_TABS.ASSESSMENT]: neuroAssessmentRequiredFields(consult),
    [CONSULT_TABS.SIGNATURE]: teleNeuroSignatureRequiredFields(
      consult,
      consultFacility,
    ),
  },
  [ServiceTypeEnum.CLINIC]: {
    [CONSULT_TABS.DEMOGRAPHICS]: [
      demographicsFields.firstName,
      demographicsFields.lastName,
    ],
    [CONSULT_TABS.ASSESSMENT]: [clinicAssessmentFields.impressionPredefined],
    [CONSULT_TABS.SIGNATURE]:
      consult?.type === ServiceTypeEnum.CLINIC
        ? []
        : [signatureFields.totalFaceToFaceTime],
  },
  [ServiceTypeEnum.EEG]: eegRequiredFields(consult, consultFacility),
  [ServiceTypeEnum.PEDS_EEG]: eegRequiredFields(consult, consultFacility, true),
});

export const consultErrors = (consult, consultType, consultFacility) => {
  const requiredTabs =
    requiredConsultFields(consult, consultFacility)[consultType] ?? {};

  return Object.entries(requiredTabs).reduce((res, [tabKey, tabElements]) => {
    const tabError = tabElements?.reduce(
      (res2, { name }) =>
        isEmptyValue(consult[tabKey][name]) ? [...res2, name] : res2,
      [],
    );
    return tabError.length ? { ...res, [tabKey]: tabError } : res;
  }, {});
};
